// React Query hooks + auth helpers over the API client.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clearToken } from "../auth/token.js";
import { api } from "./client.js";
import type {
  CommentDTO,
  IdentitySummary,
  IngestInfoDTO,
  MeDTO,
  MyStreamDTO,
  StreamCardDTO,
  StreamDetailDTO,
  StreamWithSecretsDTO,
} from "./types.js";

// /auth/me returns the bare MeDTO when logged in, or { user: null } when anonymous.
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<MeDTO | null> => {
      const data = await api.get<MeDTO | { user: null }>("/auth/me");
      if (data && "user" in data && data.user === null) return null;
      return data as MeDTO;
    },
    staleTime: 30_000,
  });
}

export function useSetupStatus() {
  return useQuery({
    queryKey: ["setup-status"],
    queryFn: () => api.get<{ initialized: boolean }>("/setup/status"),
    staleTime: 60_000,
  });
}

export function useFeatured() {
  return useQuery({
    queryKey: ["featured"],
    queryFn: () => api.get<{ items: StreamCardDTO[] }>("/api/streams/featured"),
    refetchInterval: 15_000,
  });
}

export function useStream(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: ["stream", idOrSlug],
    queryFn: () => api.get<StreamDetailDTO>(`/api/streams/${idOrSlug}`),
    enabled: !!idOrSlug,
    refetchInterval: 20_000,
  });
}

// Owner-facing push/ingest info (server URL + key) for an existing stream. Must be a real uuid.
export function useStreamIngest(uuid: string | undefined) {
  return useQuery({
    queryKey: ["stream-ingest", uuid],
    queryFn: () => api.get<IngestInfoDTO>(`/api/streams/${uuid}/ingest`),
    enabled: !!uuid,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post<void>("/auth/logout").catch(() => {});
      clearToken(); // cookie-less: drop the stored session token
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.patch<MeDTO>("/api/me", { displayName }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post<{ avatarUrl: string }>("/api/me/avatar", fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useIdentities(enabled: boolean) {
  return useQuery({
    queryKey: ["identities"],
    queryFn: () => api.get<IdentitySummary[]>("/me/identities"),
    enabled,
  });
}

export function usePostComment(uuid: string) {
  return useMutation({
    mutationFn: (content: string) => api.post<CommentDTO>(`/api/streams/${uuid}/comments`, { content }),
  });
}

export function useCreateStream() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      slug?: string;
      description?: string;
      scheduledStartAt?: string;
      recordEnabled?: boolean;
      preferredRegion?: string;
    }) => api.post<StreamWithSecretsDTO>("/api/streams", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-streams"] }),
  });
}

// ─────────────────────────── Creator Studio ───────────────────────────

export function useMyStreams() {
  return useQuery({
    queryKey: ["my-streams"],
    queryFn: () => api.get<{ items: MyStreamDTO[] }>("/api/me/streams"),
    refetchInterval: 15_000,
  });
}

export interface UpdateStreamBody {
  title?: string;
  slug?: string | null;
  description?: string | null;
  scheduledStartAt?: string | null;
  // recordEnabled is set only at create (drives the CF recording mode) — not editable.
}

export function useUpdateStream() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, body }: { uuid: string; body: UpdateStreamBody }) =>
      api.patch<StreamDetailDTO>(`/api/streams/${uuid}`, body),
    onSuccess: (_d, { uuid }) => {
      qc.invalidateQueries({ queryKey: ["my-streams"] });
      qc.invalidateQueries({ queryKey: ["stream", uuid] });
    },
  });
}

export function useEndStream() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => api.post<void>(`/api/streams/${uuid}/end`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-streams"] }),
  });
}

export function useRegenerateKey() {
  return useMutation({
    mutationFn: (uuid: string) =>
      api.post<{ streamKey: string }>(`/api/streams/${uuid}/regenerate-key`),
  });
}
